// Hook fuer Y.Array<Link>. Observed live, plus create/delete.

import { type Link, type LinkRouting, type LinkType, isLink } from "@ms-whiteboard/shared";
import { useCallback, useEffect, useState } from "react";
import type * as Y from "yjs";

export interface UseLinksApi {
  links: Link[];
  createLink: (from: string, to: string, type?: LinkType) => void;
  deleteLink: (id: string) => void;
  deleteLinksFor: (objectIds: ReadonlySet<string>) => void;
  /** Optional O4: Link-Type-Aenderung im Inspector. */
  updateLinkType: (id: string, type: LinkType) => void;
  /** Welle 3 Findings: Verlaufs-Stil pro Pfeil. */
  updateLinkRouting: (id: string, routing: LinkRouting) => void;
}

export function useLinks(
  yArray: Y.Array<Link> | null,
  userId: string | null,
  newId: () => string,
): UseLinksApi {
  const [links, setLinks] = useState<Link[]>([]);

  useEffect(() => {
    if (!yArray) {
      setLinks([]);
      return;
    }
    const sync = (): void => {
      const next: Link[] = [];
      for (const value of yArray.toArray()) {
        if (isLink(value)) next.push(value);
      }
      setLinks(next);
    };
    sync();
    yArray.observe(sync);
    return () => yArray.unobserve(sync);
  }, [yArray]);

  const createLink = useCallback(
    (from: string, to: string, type: LinkType = "relates-to"): void => {
      if (!yArray || !userId) return;
      if (from === to) return;
      const link: Link = {
        id: newId(),
        from,
        to,
        type,
        createdBy: userId,
        createdAt: Date.now(),
      };
      yArray.push([link]);
    },
    [yArray, userId, newId],
  );

  const deleteLink = useCallback(
    (id: string): void => {
      if (!yArray) return;
      const all = yArray.toArray();
      for (let i = all.length - 1; i >= 0; i -= 1) {
        const raw = all[i];
        if (isLink(raw) && raw.id === id) {
          yArray.delete(i, 1);
          return;
        }
      }
    },
    [yArray],
  );

  const deleteLinksFor = useCallback(
    (objectIds: ReadonlySet<string>): void => {
      if (!yArray || objectIds.size === 0) return;
      const all = yArray.toArray();
      const doc = yArray.doc;
      const op = (): void => {
        for (let i = all.length - 1; i >= 0; i -= 1) {
          const raw = all[i];
          if (isLink(raw) && (objectIds.has(raw.from) || objectIds.has(raw.to))) {
            yArray.delete(i, 1);
          }
        }
      };
      if (doc) doc.transact(op);
      else op();
    },
    [yArray],
  );

  const updateLinkType = useCallback(
    (id: string, type: LinkType): void => {
      if (!yArray) return;
      const all = yArray.toArray();
      const doc = yArray.doc;
      const op = (): void => {
        for (let i = all.length - 1; i >= 0; i -= 1) {
          const raw = all[i];
          if (isLink(raw) && raw.id === id) {
            if (raw.type === type) return;
            yArray.delete(i, 1);
            yArray.insert(i, [{ ...raw, type }]);
            return;
          }
        }
      };
      if (doc) doc.transact(op);
      else op();
    },
    [yArray],
  );

  const updateLinkRouting = useCallback(
    (id: string, routing: LinkRouting): void => {
      if (!yArray) return;
      const all = yArray.toArray();
      const doc = yArray.doc;
      const op = (): void => {
        for (let i = all.length - 1; i >= 0; i -= 1) {
          const raw = all[i];
          if (isLink(raw) && raw.id === id) {
            if (raw.routing === routing) return;
            yArray.delete(i, 1);
            yArray.insert(i, [{ ...raw, routing }]);
            return;
          }
        }
      };
      if (doc) doc.transact(op);
      else op();
    },
    [yArray],
  );

  return { links, createLink, deleteLink, deleteLinksFor, updateLinkType, updateLinkRouting };
}
